import { useEffect, useState } from "react";
import { Box, Alert, CircularProgress, Typography } from "@mui/material";
import ResellerDashboard from "../../components/resellers/ResellerDashboard";
import { resellerFetch } from "../../api/client";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

/**
 * Wave 1.1 — Route wrapper that resolves the logged-in reseller's identity
 * (GET /resellers/me) and renders the real ResellerDashboard component.
 * Replaces the former "Coming soon" placeholder.
 *
 * Cookie-first auth: the httpOnly reseller_token cookie is the credential;
 * resellerFetch is same-origin so it rides along, and a dead session 401s
 * into the shared client interceptor's /reseller/login?expired=1 bounce.
 */
export default function ResellerDashboardPage() {
  const [resellerId, setResellerId] = useState<string | null>(null);
  const [resellerAccount, setResellerAccount] = useState<string | null>(null);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await resellerFetch(`${API_BASE_URL}/api/v1/resellers/me`);
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.detail || "Failed to load reseller profile");
        }
        const data = await response.json();
        if (cancelled) return;
        setResellerId(data.id);
        setResellerAccount(data.account);
      } catch (err: any) {
        if (!cancelled) setError(err.message || "Failed to load dashboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !resellerId || !resellerAccount) {
    return (
      <Box p={3}>
        <Alert severity="error">
          <Typography variant="subtitle2" gutterBottom>
            Unable to load reseller dashboard
          </Typography>
          {error || "Reseller account not found for this login."}
        </Alert>
      </Box>
    );
  }

  return <ResellerDashboard resellerId={resellerId} resellerAccount={resellerAccount} />;
}
